// Quick-action registry shared by LinkCard (swipe + long-press), Bookmarks.vue
// (routing) and the View Settings swipe picker.
//
// Pure and i18n-free on purpose: LinkCard imports this and sits in the embed
// bundle closure (tests/embedNoI18n.test.js), so everything user-facing here is
// an ID — the parent view translates IDs into labels and passes strings down.
// Dependency-free so the embed bundle does not grow by more than this file.

// What a swipe can be mapped to in View Settings. `archive` and `pin` are
// contextual (see resolveSwipeAction); `menu` opens the quick-actions sheet;
// `none` disables that direction.
export const SWIPE_ACTION_IDS = Object.freeze(['archive', 'delete', 'pin', 'remind', 'menu', 'none'])

export const DEFAULT_SWIPE_ACTIONS = Object.freeze({ right: 'archive', left: 'delete' })

// Concrete actions a swipe resolves to. `icon` is a PrimeIcons class, `tone`
// picks the swipe-layer background (LinkCard `.swipe-action.tone-*`).
export const QUICK_ACTION_META = Object.freeze({
  archive: Object.freeze({ icon: 'pi pi-inbox', tone: 'success' }),
  inbox: Object.freeze({ icon: 'pi pi-inbox', tone: 'info' }),
  delete: Object.freeze({ icon: 'pi pi-trash', tone: 'danger' }),
  pin: Object.freeze({ icon: 'pi pi-bookmark', tone: 'accent' }),
  unpin: Object.freeze({ icon: 'pi pi-bookmark-fill', tone: 'accent' }),
  remind: Object.freeze({ icon: 'pi pi-bell', tone: 'warning' }),
  menu: Object.freeze({ icon: 'pi pi-ellipsis-h', tone: 'neutral' })
})

// Bookmarks are enriched with camelCase aliases (inInbox) while raw API rows
// carry snake_case (in_inbox); read both so the resolver works on either shape.
export function isLinkInInbox(link) {
  return Boolean(link?.in_inbox ?? link?.inInbox ?? false)
}

export function isLinkPinned(link) {
  return Boolean(link?.pinned)
}

/**
 * Resolve a configured swipe action against the link it is applied to.
 *
 * The gesture is never dead: `archive` on a link that is not in the inbox
 * becomes `inbox` (move TO the inbox), and `pin` on a pinned link becomes
 * `unpin`. (The previous implementation suppressed swipe-right entirely
 * outside the Inbox view, so the whole gesture did nothing there.)
 *
 * @returns {string|null} concrete action id, or null when disabled/unknown
 */
export function resolveSwipeAction(actionId, link) {
  switch (actionId) {
    case 'archive':
      return isLinkInInbox(link) ? 'archive' : 'inbox'
    case 'pin':
      return isLinkPinned(link) ? 'unpin' : 'pin'
    case 'delete':
    case 'remind':
    case 'menu':
      return actionId
    default:
      return null
  }
}

/**
 * Validate a stored swipe mapping. Anything unknown (older localStorage,
 * a removed action id, garbage) falls back to the default for that side, so a
 * stale preference can never leave a swipe pointing at nothing by accident.
 */
export function normalizeSwipeSettings(raw) {
  const out = { ...DEFAULT_SWIPE_ACTIONS }
  if (raw && typeof raw === 'object') {
    for (const side of ['right', 'left']) {
      if (SWIPE_ACTION_IDS.includes(raw[side])) out[side] = raw[side]
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Long press
// ---------------------------------------------------------------------------

export const LONG_PRESS_MS = 450
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10

/**
 * Timer-based long-press detector, kept free of DOM/Vue so it is unit-testable
 * with fake timers. The caller feeds it touch coordinates:
 *
 *   start(x, y)  — finger down; arms the timer
 *   move(x, y)   — cancels once the finger drifts past `tolerancePx`
 *   cancel()     — touchcancel / horizontal swipe took over
 *   end()        — finger up; returns true when the press HAD fired so the
 *                  caller can suppress the synthesized click
 */
export function createLongPressTracker({
  onFire,
  delayMs = LONG_PRESS_MS,
  tolerancePx = LONG_PRESS_MOVE_TOLERANCE_PX,
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancelScheduled = (handle) => clearTimeout(handle)
} = {}) {
  let timer = null
  let startX = 0
  let startY = 0
  let fired = false

  const clear = () => {
    if (timer !== null) {
      cancelScheduled(timer)
      timer = null
    }
  }

  return {
    start(x, y) {
      clear()
      fired = false
      startX = x
      startY = y
      timer = schedule(() => {
        timer = null
        fired = true
        onFire()
      }, delayMs)
    },
    move(x, y) {
      if (timer === null) return
      if (Math.abs(x - startX) > tolerancePx || Math.abs(y - startY) > tolerancePx) clear()
    },
    cancel() {
      clear()
      fired = false
    },
    end() {
      clear()
      const wasFired = fired
      fired = false
      return wasFired
    },
    get pending() {
      return timer !== null
    }
  }
}
